/* eslint-disable jsx-a11y/label-has-associated-control */
import {
  EditOutlined,
  LinkOutlined,
  MoreOutlined,
  SwapOutlined
} from "@ant-design/icons";
import { useMutation } from "@apollo/client";
import {
  Button,
  message,
  Popover,
  Radio,
  Space,
  Switch,
  Tag,
  Tooltip
} from "antd";
import {
  AcceptanceStatus,
  ESubmittalStatus,
  EUserRoleName,
  EWorkflowStatusDataBlock,
  EmailInviteStatus,
  ErrorMessages,
  GanttActivityTypes,
  IntegrationAuthStates,
  IntegrationSystemNameMap,
  SourceNames,
  SubmittalStatusToStrMap
} from "constants/index";
import { ProjectContext } from "context/ProjectProvider";
import {
  SubscriptionContext,
  RoleSubscriptionEnum
} from "context/SubscriptionProvider";
import {
  forwardRef,
  ReactElement,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState
} from "react";
import { Link, useParams } from "react-router-dom";
import {
  MUTATION_CANCEL_SUBSCRIPTION_INVITATION,
  MUTATION_RESEND_EMAIL_INVITE,
  MUTATION_SEND_ACC_PROJECT_LEVEL_EMAIL_INVITE,
  MUTATION_SEND_ACC_SUBSCRIPTION_LEVEL_EMAIL_INVITE
} from "services/graphQL/mutations";
import {
  getProjectUserSources,
  getSubscriptionUserSources,
  sortAlphanumeric,
  sortLinkedItems
} from "utils/utils";
import "./cell-renders.css";
import { agGridCheckFieldEditable } from "components/submittal-details/helpers";
import { ColDef, ICellRendererParams } from "ag-grid-enterprise";

import "@react-pdf-viewer/core/lib/styles/index.css";
import { DateUtils } from "utils/dateutils";
import DateBlockSubmittalImpactVisualisation from "components/date-block/submittal-impact-visualise";
import ArrowUpBox from "components/svg-icons/arrow-right-up-box-line";
import {
  CopyIcon,
  SubmittalCiqIcon,
  MaterialCiqIcon,
  ScheduleCiqIcon
} from "components/svg-icons";
import DefaultTag from "components/widgets/default-tag";
import InfoIconPath from "assets/svg/i-icon.svg";
import CriticalActivityIconPath from "assets/svg/critical_activity.svg";
import QuestionMarkIcon from "assets/svg/questionmark-icon.svg";
import CopyDBTemplate from "popups/copy-db-template";
import UserTag from "components/user-tag/user-tag";
import modal from "antd/lib/modal";
import ProcoreLogoIcon from "components/svg-icons/procore-logo-icon";
import { IntegrationType } from "pages/subscription-settings/integrations-tab-new/utils";
import AutodeskLogoIcon from "components/svg-icons/autodesk-logo-icon";
import AuthenticationExpiredIcon from "components/svg-icons/authentication-expired-icon";
import { RiskLevelType } from "components/date-block/models";
import PersonEditIcon from "components/svg-icons/person-edit-icon";
import PersonCheckIcon from "components/svg-icons/person-check-icon";
import LinkIconLogPage from "components/svg-icons/link-icon-log-page";
import VoidSubmittalIcon from "components/svg-icons/void-submittal-icon";
import TickIcon from "components/svg-icons/tick-icon";
import ClockIcon from "components/svg-icons/clock-icon";
import TitleDescriptionTooltip from "components/widgets/title-description-tooltip";
import CriticalActivityIcon from "components/svg-icons/critical-activity-icon";
import CriticalActivityTag from "pages/schedule/schedule-page/critical-activity-tag";
import CriticalActivityIndicator from "components/links/critical-activity-indicator";
import DeletedSubmittalTag from "components/deleted-submittal-tag";
import OffsetIcon from "components/svg-icons/offset-icon";
import ActivityOffsetTag from "components/activity-offset-tag";
import type { CustomCellRendererProps } from "ag-grid-react";
import CommentIcon from "components/svg-icons/comment-icon";
import BIM360logo from "../../assets/image/bim360-logo.png";

interface TruncatedTextProps {
  text: string;
  onMouseEvent: (
    mouseEventType: "enter" | "leave",
    isTruncated: boolean
  ) => void;
  className?: string;
  disableMouseEvent?: boolean;
  dataTestId?: string;
}

const TruncatedText = forwardRef<HTMLDivElement, TruncatedTextProps>(
  (
    {
      text,
      onMouseEvent,
      className = "",
      disableMouseEvent = false,
      dataTestId
    },
    ref
  ) => {
    const innerRef = useRef<HTMLDivElement | null>(null);

    const checkTruncation = useCallback(() => {
      let isTruncated = false;
      if (innerRef.current) {
        isTruncated =
          innerRef.current.scrollWidth > innerRef.current.clientWidth;
        return isTruncated;
      }
      return isTruncated;
    }, []);

    if (disableMouseEvent) {
      return (
        <div
          className={`tooltip-disabled truncated-text-container truncate ${className}`}
          data-testid={dataTestId}
        >
          {text}
        </div>
      );
    }

    return (
      <div
        data-testid={dataTestId}
        ref={(node) => {
          if (typeof ref === "function") {
            ref(node);
          } else if (ref) {
            // eslint-disable-next-line no-param-reassign
            ref.current = node;
          }
          innerRef.current = node;
        }}
        className={`truncated-text-container truncate ${className}`}
        onMouseEnter={() => {
          onMouseEvent("enter", checkTruncation());
        }}
        onMouseLeave={() => {
          onMouseEvent("leave", checkTruncation());
        }}
      >
        {text}
      </div>
    );
  }
);

export function TruncatedTextWithTooltip(props: {
  text: string | null | undefined;
  tooltipContent?: any;
  className?: string;
  disableTooltip?: boolean;
  dataTestId?: string;
}) {
  const {
    text,
    tooltipContent,
    disableTooltip = false,
    className = "",
    dataTestId
  } = props;
  const [showTooltip, setShowTooltip] = useState(false);
  const [makeTooltipVisible, setMakeTooltipVisible] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const handleMouseEvent = (mouseEvent: string, isTruncated: boolean) => {
    clearTimeout(timeoutRef.current);

    if (mouseEvent === "enter" && isTruncated) {
      setShowTooltip(true);
      // give slight delay for ant.design to place the tooltip at the right location, else it will flash at the top-left corner of the page for a moment
      timeoutRef.current = setTimeout(() => {
        setMakeTooltipVisible(true);
      }, 0);
    } else if (showTooltip) {
      setShowTooltip(false);
      setMakeTooltipVisible(false);
    }
  };

  const isTooltipDisabled =
    typeof disableTooltip === "boolean" && disableTooltip === true;

  return (
    <Tooltip
      title={
        tooltipContent || <div className="text-xs p-0.5">{text}</div> || ""
      }
      open={showTooltip}
      placement="top"
      overlayClassName={`${makeTooltipVisible ? "" : "hidden"}`}
      destroyTooltipOnHide
      autoAdjustOverflow
    >
      <TruncatedText
        dataTestId={dataTestId}
        className={`max-w-full w-full ${className}`}
        text={text ?? ""}
        onMouseEvent={handleMouseEvent}
        disableMouseEvent={isTooltipDisabled}
      />
    </Tooltip>
  );
}

export function truncatedTextCellRenderer(params: any) {
  const colId = params.colDef?.colId || params.colDef?.field;
  return <TruncatedTextWithTooltip text={params.value} dataTestId={colId} />;
}

export function IdLinkComponent(props: any, disableTooltip?: boolean) {
  const { value, data, projectId, context } = props;
  const cellText =
    context.projectDetails?.spec_section && data.spec_no
      ? `${data.spec_no} - ${value}`
      : `${value}`;

  return (
    <Link
      className="gridIdLink"
      to={{
        pathname: `/project/${projectId}/submittals/${data.id}`
      }}
      data-testid="index-link-component"
    >
      <TruncatedTextWithTooltip
        text={cellText}
        disableTooltip={disableTooltip}
      />
    </Link>
  );
}

export function IdLinkComponentNewModel(props: any, disableTooltip?: boolean) {
  const { value, data, projectId, context } = props;

  if (!value) return "";

  const cellText =
    context.projectDetails?.spec_section && data.spec_section.length > 0
      ? `${data.spec_section[0].number} - ${value}`
      : `${value}`;

  return (
    <Link
      className="gridIdLink"
      to={{
        pathname: `/project/${projectId}/submittals/${data.id}`
      }}
      data-testid="index-link-component-new-model"
    >
      <TruncatedTextWithTooltip
        text={cellText}
        disableTooltip={disableTooltip}
      />
    </Link>
  );
}

export function MaterialIdLinkCellRenderer(params: any) {
  const { data, value, context } = params;
  return (
    <Link
      className="gridIdLink"
      to={{
        pathname: `/project/${context.projectId}/materials/${data.id}`
      }}
      data-testid="index-link-material-link"
    >
      <TruncatedTextWithTooltip text={value} />
    </Link>
  );
}

export function IdLinkProjectSumbittalListComponent(props: any) {
  const { value, data, disabled = false, showToolTipMsg, tooltipMsg } = props;

  if (disabled && showToolTipMsg) {
    return (
      <TruncatedTextWithTooltip text={value} tooltipContent={tooltipMsg} />
    );
  }
  if (disabled && !showToolTipMsg) {
    return <TruncatedTextWithTooltip text={value} disableTooltip />;
  }
  return (
    <Link
      to={{
        pathname: `project/${data.id}/dashboard`
      }}
      data-testid="index-link-project-submittal-list"
    >
      <TruncatedTextWithTooltip text={value} />
    </Link>
  );
}

export const dateCellRenderer = (params: any) => {
  const formattedDate = DateUtils.format(params.value);
  return (
    <div className="w-full flex items-center">
      <div className="grow min-w-0">
        <TruncatedTextWithTooltip text={formattedDate} />
      </div>
      <div className="px-1">
        <EditOutlined className="cell-edit-icon" />
      </div>
    </div>
  );
};

export const dueDateCellRenderer = (params: any, disableTooltip: boolean) => {
  const colId = params.colDef?.colId || params.colDef?.field;
  const todayStartDateObject = DateUtils.dateTimeObj().startOf("day");
  const dueDateObject = DateUtils.dateTimeObj(params.value);
  const isPast = dueDateObject.isBefore(todayStartDateObject);
  const cellValueClassnames = isPast ? "date-block-date-risk" : "";
  return (
    <div className="w-full flex items-center">
      <div className="grow min-w-0">
        <TruncatedTextWithTooltip
          text={params.value}
          className={cellValueClassnames}
          disableTooltip={disableTooltip}
          dataTestId={colId}
        />
      </div>
      <div className="px-1">
        <EditOutlined className="cell-edit-icon" />
      </div>
    </div>
  );
};

const dueDateHeaderTemplate = (title: string) => ({
  template: `<div class="ag-cell-label-container" role="presentation">
        <span data-ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
        <span data-ref="eFilterButton" class="ag-header-icon ag-header-cell-filter-button"></span>
        <div data-ref="eLabel" class="ag-header-cell-label" role="presentation">
          <span data-ref="eText" class="ag-header-cell-text" role="columnheader"></span>
          <img src='${InfoIconPath}' title='${title}' class="ag-header-icon info-icon">
          <span data-ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>
          <span data-ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>
          <span data-ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>
          <span data-ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>
          <span data-ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
        </div>
      </div>`
});

export const logHeaderTemplateWithQuestionMark = () => ({
  template: `<div class="ag-cell-label-container" role="presentation">
        <span data-ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
        <span data-ref="eFilterButton" class="ag-header-icon ag-header-cell-filter-button"></span>
        <div data-ref="eLabel" class="ag-header-cell-label" role="presentation">
          <span data-ref="eText" class="ag-header-cell-text" role="columnheader"></span>
          <span class="grow text-right"><img src='${QuestionMarkIcon}' class="ag-header-icon -mt-0.5 mr-1"></span>
          <span data-ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>
          <span data-ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>
          <span data-ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>
          <span data-ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>
          <span data-ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
        </div>
      </div>`
});

const criticalActivityHeaderTemplate = () => ({
  template: `<div class="ag-cell-label-container" role="presentation">
              <span data-ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
              <span data-ref="eFilterButton" class="ag-header-icon ag-header-cell-filter-button"></span>
              <div data-ref="eLabel" class="ag-header-cell-label" role="presentation">
                <span data-ref="eText" class="ag-header-cell-text hidden" role="columnheader"></span>
                <img src='${CriticalActivityIconPath}' title='' class="ag-header-icon info-icon">
                <span data-ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>
                <span data-ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>
                <span data-ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>
                <span data-ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>
                <span data-ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
              </div>
            </div>`
});

export const submittalDueDateHeaderComponent = (params: any) => {
  const { context } = params;
  const headerTitle = context?.submittalHeaderMap?.due_date || "Due Date";
  const projectCompletionTitle =
    context?.submittalHeaderMap?.projected_last_milestone_date ||
    "Projected Completion Date";
  const deadlineTitle =
    context?.submittalHeaderMap?.final_deadline_date || "Deadline";

  const camelCaseHeaderTitle = `${headerTitle} is the minimum of ${deadlineTitle} and ${projectCompletionTitle}. `;
  return dueDateHeaderTemplate(camelCaseHeaderTitle);
};

export const materialDueDateHeaderComponent = (params: any) => {
  const { context } = params;
  const headerTitle = context?.materialHeaderMap?.due_date || "Due Date";
  const projectCompletionTitle =
    context?.materialHeaderMap?.projected_last_milestone_date ||
    "Projected Completion Date";
  const deadlineTitle =
    context?.materialHeaderMap?.final_deadline_date || "Deadline";

  const camelCaseHeaderTitle = `${headerTitle} is the minimum of ${deadlineTitle} and ${projectCompletionTitle}. `;

  return dueDateHeaderTemplate(camelCaseHeaderTitle);
};

export const bidPackageDueDateHeaderComponent = (params: any) => {
  const { displayName, context } = params;
  const headerTitle = displayName || "Due Date";
  const projectCompletionTitle =
    context?.featureHeaderMap?.projected_last_milestone_date ||
    "Projected Completion Date";
  const deadlineTitle =
    context?.featureHeaderMap?.final_deadline_date || "Deadline";

  const camelCaseHeaderTitle = `${headerTitle} is the minimum of ${deadlineTitle} and ${projectCompletionTitle}. `;

  return dueDateHeaderTemplate(camelCaseHeaderTitle);
};

export const leadTimeHeaderComponent = () => {
  const title = "In Calendar days, including weekends and holidays.";
  return dueDateHeaderTemplate(title);
};

export const criticalActivityHeaderComponent = () => {
  return criticalActivityHeaderTemplate();
};

export const projectDateCellRenderer = (params: any) => {
  if (params.data?.status_id === AcceptanceStatus.ACTIVE) {
    return dateCellRenderer(params);
  }
  return null;
};

export const projectOverviewCellRenderer = ({ data }: any) => {
  const showNumber = (num: number = 0) => (
    <div className={num >= 1000 ? "text-xs" : "text-sm"}>{num}</div>
  );

  function CountDetail({ num, icon }: { num: number; icon: ReactElement }) {
    return (
      <div className="flex items-center space-x-1 w-12">
        {icon} {showNumber(num)}
      </div>
    );
  }

  const popOver = (
    <div className="flex-col w-56 text-xs space-y-1">
      <div className="flex justify-between w-full">
        <div
          className="flex items-center"
          data-testid="submittals-count-parent"
        >
          <SubmittalCiqIcon />
          <div className="pl-1 opacity-60"> Submittals</div>
        </div>
        <div className="font-medium" data-testid="submittals-count">
          {data.submittals_count}
        </div>
      </div>
      <div className="flex justify-between w-full">
        <div className="flex items-center" data-testid="materials-count-parent">
          <MaterialCiqIcon />
          <div className="pl-1 opacity-60"> Materials</div>
        </div>
        <div className="font-medium" data-testid="materials-count">
          {data.materials_count}
        </div>
      </div>
      <div className="flex justify-between w-full">
        <div className="flex items-center" data-testid="schedules-count-parent">
          <ScheduleCiqIcon />
          <div className="pl-1 opacity-60"> Schedule Versions</div>
        </div>
        <div className="font-medium" data-testid="schedules-count">
          {data.schedules_count}
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-2">
      <Popover placement="right" content={popOver} trigger="hover">
        <div className="flex justify-evenly items-center pl-1 h-7 space-x-1 border border-solid border-ciq-gray-100 rounded-sm opacity-80">
          <CountDetail
            num={data.submittals_count}
            icon={<SubmittalCiqIcon />}
          />
          <CountDetail num={data.materials_count} icon={<MaterialCiqIcon />} />
          <CountDetail num={data.schedules_count} icon={<ScheduleCiqIcon />} />
        </div>
      </Popover>
    </div>
  );
};

export function CellChilderenRendererWithEditButton(props: any) {
  const { onEditCell, children } = props;
  return (
    <div className="w-full max-w-full overflow-hidden flex items-center">
      <div className="grow min-w-0 box-content overflow-hidden mr-1">
        {children ?? ""}
      </div>
      <div className="w-[24px] text-right">
        <EditOutlined
          className="cell-edit-icon"
          onClick={() => {
            if (onEditCell) {
              onEditCell();
            }
          }}
        />
      </div>
    </div>
  );
}
export function CellValueRendererWithEditIcon(
  params: CustomCellRendererProps,
  disableTooltip: boolean
) {
  const { value, valueFormatted, context, column, data, node, colDef } = params;
  const text = valueFormatted || value;

  const editDiv = (
    <EditOutlined
      className="cell-edit-icon"
      onClick={() => {
        if (context.onEditCell)
          context.onEditCell({
            rowIndex: node.rowIndex!,
            colKey: column!.getId(),
            data
          });
      }}
    />
  );

  if (!["string", "number"].includes(typeof text))
    return (
      <div className="w-full max-w-full h-full overflow-hidden flex items-center">
        <div className="grow min-w-0" />
        <div>{editDiv}</div>
      </div>
    );

  const colId = colDef?.colId || colDef?.field;
  return (
    <div className="w-full max-w-full h-full overflow-hidden flex items-center">
      <div className="grow min-w-0">
        <TruncatedTextWithTooltip
          text={text}
          disableTooltip={disableTooltip}
          dataTestId={colId}
        />
      </div>
      <div>{editDiv}</div>
    </div>
  );
}

export function CellCurrencyRendererWithEditIcon(params: any) {
  const { value, context, node, column, data } = params;
  return (
    <div className="w-full flex items-center">
      <TruncatedTextWithTooltip text={value} />
      <div>
        <EditOutlined
          className="cell-edit-icon"
          onClick={() => {
            if (context.onEditCell)
              context.onEditCell({
                rowIndex: node.rowIndex!,
                colKey: column!.getId(),
                data
              });
          }}
        />
      </div>
    </div>
  );
}

function EditableCell({ children }: { children?: React.ReactNode }) {
  return (
    <div className="flex">
      <div className="grow overflow-hidden truncate">{children ?? ""}</div>
      <div className="w-[13px]">
        <EditOutlined className="cell-edit-icon" />
      </div>
    </div>
  );
}

export function specSectionLabel(props: {
  specNumber?: string | number | null;
  specName?: string | null;
}): string {
  const normalize = (value?: string | number | null): string => {
    if (value === null || value === undefined) return "";
    return String(value).trim();
  };

  const specNumberStr = normalize(props.specNumber);
  const specNameStr = normalize(props.specName);

  if (specNumberStr && specNameStr) {
    return `${specNumberStr} - ${specNameStr}`;
  }

  return specNumberStr || specNameStr;
}

export function specSectionRenderer(params: any, disableTooltip?: boolean) {
  const { data, specNoField, specNameField, context } = params;
  const isTooltipDisabled =
    typeof disableTooltip === "boolean" && disableTooltip === true;
  try {
    const specSectionStr = specSectionLabel({
      specNumber: data[specNoField],
      specName: data[specNameField]
    });

    return (
      <div className="w-full h-full flex items-center">
        <div className="grow truncate  items-center">
          <TruncatedTextWithTooltip
            text={specSectionStr}
            disableTooltip={isTooltipDisabled}
          />
        </div>
        <div>
          <EditOutlined
            className="cell-edit-icon"
            onClick={() => {
              context.onEditCell({
                rowIndex: params.node.rowIndex!,
                colKey: params.column!.getId(),
                data
              });
            }}
          />
        </div>
      </div>
    );
  } catch (ex) {
    return "";
  }
}

export function userNameRenderer(params: any) {
  const { data } = params;
  try {
    let userNameStr = data?.user?.first_name || "";
    if (data?.user?.last_name) {
      userNameStr += `  ${data?.user?.last_name}`;
    }
    return (
      <div className="w-full flex">
        <TruncatedTextWithTooltip text={userNameStr} />
        <div>
          <EditOutlined className="cell-edit-icon" />
        </div>
      </div>
    );
  } catch (ex) {
    return "";
  }
}

export function userRoleRenderer(params: any) {
  const { data } = params;
  try {
    const role = data?.subscription_permission?.name || "";

    return (
      <div className="w-full flex">
        <TruncatedTextWithTooltip text={role} />
        <div>
          <EditOutlined className="cell-edit-icon" />
        </div>
      </div>
    );
  } catch (ex) {
    return "";
  }
}

export function userCompanyRenderer(params: any) {
  const { data } = params;
  try {
    const company = data?.subscription_vendor?.name || "";

    return (
      <div className="w-full flex">
        <TruncatedTextWithTooltip text={company} />
        <div>
          <EditOutlined className="cell-edit-icon" />
        </div>
      </div>
    );
  } catch (ex) {
    return "";
  }
}

export function projectUserRoleRenderer(params: any) {
  const { data } = params;
  try {
    const role = data.project_role?.name || "";

    return (
      <div className="w-full flex">
        <TruncatedTextWithTooltip text={role} />
        <div>
          <EditOutlined className="cell-edit-icon" />
        </div>
      </div>
    );
  } catch (ex) {
    return "";
  }
}

export function projectCompanyRoleRenderer(params: any) {
  const { data } = params;
  try {
    const role = data?.project_vendors_type?.name || "";

    return (
      <div className="w-full flex">
        <TruncatedTextWithTooltip text={role} />
        <div>
          <EditOutlined className="cell-edit-icon" />
        </div>
      </div>
    );
  } catch (ex) {
    return "";
  }
}

function InviteStatusText(props: {
  statusId: number;
  userId: string;
  inviteType: string | null;
  isCIQUser: boolean;
  isInviteBtnVisible?: boolean;
  context?: any;
  userData?: any;
  roleId?: any;
}) {
  const {
    statusId,
    userId,
    inviteType,
    isCIQUser,
    isInviteBtnVisible,
    context,
    userData,
    roleId
  } = props;

  const { subscriptionRole } = useContext(SubscriptionContext);
  const { tokenContents } = useContext(ProjectContext);
  const { gqlClientForProject } = useContext(ProjectContext);
  const [cancelprogess, setCancelProgress] = useState(false);
  const [resendprogess, setResendProgress] = useState(false);

  const [resendAccProjectUserInviteMutation] = useMutation(
    MUTATION_SEND_ACC_PROJECT_LEVEL_EMAIL_INVITE,
    {
      variables: {
        user_id: userId,
        role_id: roleId,
        invited_user_company_id: userData?.company_id
      },
      client: gqlClientForProject
    }
  );

  const [resendAccSubscriptionUserInviteMutation] = useMutation(
    MUTATION_SEND_ACC_SUBSCRIPTION_LEVEL_EMAIL_INVITE,
    {
      variables: {
        user_id: userId,
        permission_id: roleId,
        invited_user_company_id: userData?.company_id
      }
    }
  );

  const [resendInviteMutation] = useMutation(MUTATION_RESEND_EMAIL_INVITE, {
    variables: {
      user_id: userId,
      invited_user_company_id: userData?.company_id
    },
    client: gqlClientForProject
  });

  const [resendSubscriptionInviteMutation] = useMutation(
    MUTATION_RESEND_EMAIL_INVITE,
    {
      variables: {
        user_id: userId,
        invited_user_company_id: userData?.company_id
      }
    }
  );
  const [cancelSubscriptionInviteMutattion] = useMutation<any>(
    MUTATION_CANCEL_SUBSCRIPTION_INVITATION,
    {
      variables: { user_id: userId }
    }
  );

  const resendInvite = () => {
    setResendProgress(true);

    if (inviteType === "Subscription") {
      (isCIQUser
        ? resendSubscriptionInviteMutation()
        : resendAccSubscriptionUserInviteMutation()
      )
        .then((res) => {
          if (res.data) {
            message.success(res.data.resend_email_invite.message);
          }
          if (res.errors) {
            message.error(res.errors[0].message);
          }
        })
        .finally(() => setResendProgress(false));
    } else {
      (isCIQUser
        ? resendInviteMutation()
        : resendAccProjectUserInviteMutation()
      )
        .then((res) => {
          if (res.data) {
            message.success(res.data.resend_email_invite.message);
          }
          if (res.errors) {
            message.error(res.errors[0].message);
          }
        })
        .finally(() => setResendProgress(false));
    }
  };

  const cancelSubscriptionInvite = () => {
    setCancelProgress(true);
    cancelSubscriptionInviteMutattion()
      .then((res) => {
        if (res.data) {
          message.success(res.data.cancel_subscription_invite.message);
        }
        if (res.errors) {
          message.error(res.errors[0].message);
        }
      })
      .finally(() => setCancelProgress(false));
  };

  switch (statusId) {
    case AcceptanceStatus.INVITED:
      return (
        <div className="flex space-x-2 items-baseline">
          <p className="margin-0 captialize-first text-red-600">Pending</p>
          {inviteType === "ProjectUser" &&
            tokenContents &&
            tokenContents.role ===
              EUserRoleName[EUserRoleName.gc_project_admin] && (
              <div className=" space-x-2">
                <Button
                  loading={resendprogess}
                  disabled={resendprogess || cancelprogess}
                  onClick={resendInvite}
                  data-testid="index-button-pending-resend"
                >
                  Resend
                </Button>
              </div>
            )}

          {inviteType === "Subscription" &&
            subscriptionRole >= RoleSubscriptionEnum.subscription_admin && (
              <div className=" space-x-2">
                <Button
                  loading={resendprogess}
                  disabled={resendprogess || cancelprogess}
                  onClick={resendInvite}
                  data-testid="index-button-subscription-admin-resend"
                >
                  Resend
                </Button>
                <Button
                  loading={cancelprogess}
                  disabled={resendprogess || cancelprogess}
                  onClick={cancelSubscriptionInvite}
                  data-testid="index-button-subscription-admin-cancel"
                >
                  Cancel Invite
                </Button>
              </div>
            )}
        </div>
      );
    case AcceptanceStatus.ACTIVE:
      return (
        <p
          className="margin-0 captialize-first text-green-600"
          data-testid="index-button-invite-status"
        >
          Active
        </p>
      );
    case AcceptanceStatus.DEACTIVATED:
      return (
        <p
          className="margin-0 captialize-first text-ciq-gray-800"
          data-testid="index-button-invite-status"
        >
          Inactive
        </p>
      );
    case AcceptanceStatus.NOT_INVITED:
      return (
        <div className="flex space-x-2 items-baseline">
          <p
            className="margin-0 captialize-first text-ciq-gray-800"
            data-testid="index-button-invite-status"
          >
            Not Invited
          </p>
          {inviteType === "Subscription" &&
            isInviteBtnVisible &&
            subscriptionRole >= RoleSubscriptionEnum.subscription_admin && (
              <Button
                data-testid="index-button-invite-subscription-admin"
                onClick={() => {
                  context?.setInviteAccUser({
                    showInviteModel: true,
                    inviteUserData: userData
                  });
                }}
              >
                Send Invite
              </Button>
            )}

          {inviteType === "ProjectUser" &&
            isInviteBtnVisible &&
            tokenContents &&
            tokenContents.role ===
              EUserRoleName[EUserRoleName.gc_project_admin] && (
              <div className=" space-x-2">
                <Button
                  data-testid="index-button-invite-gc-project-admin"
                  onClick={() => {
                    context?.setInviteAccUser({
                      showInviteModel: true,
                      inviteUserData: userData
                    });
                  }}
                >
                  Send Invite
                </Button>
              </div>
            )}
        </div>
      );
    default:
      return null;
  }
}

export const projectUserActionRenderer = (params: any) => {
  const { data, context } = params;

  const infoMsg =
    data.status_id === AcceptanceStatus.ACTIVE
      ? "No permission to deactivate"
      : "No permission to activate";

  const confirmMsg =
    data.status_id === AcceptanceStatus.ACTIVE
      ? "Are you sure you want to deactivate this user from the project?"
      : "Are you sure you want to make this user active? ";

  const onToggleAction = (isChecked: boolean) => {
    modal.confirm({
      content: <div className="pl-10">{confirmMsg}</div>,
      onOk() {
        context?.userAction(data?.user?.id, isChecked);
      },
      okText: "Confirm"
    });
  };

  if (
    data.status_id === AcceptanceStatus.INVITED ||
    context?.currentUser?.email === data?.user?.email
  ) {
    return "";
  }

  try {
    return (
      <div className="w-full h-full flex items-center custom-switch">
        <Tooltip title={!context?.isGCADmin ? infoMsg : ""}>
          <Switch
            disabled={!context?.isGCADmin}
            checked={data.status_id === AcceptanceStatus.ACTIVE}
            checkedChildren="ON"
            unCheckedChildren="OFF"
            onChange={(isChecked) => {
              onToggleAction(isChecked);
            }}
            data-testid="index-switch-user-action"
          />
        </Tooltip>
      </div>
    );
  } catch (ex) {
    return "";
  }
};

export function InviteStatusCellRenderer(props: any) {
  const { data, context } = props;
  const { value, inviteType } = props;
  let dataSources = [];
  let isCIQUser = true;
  let nonCIQsrcPresent = true;

  let isInviteBtnVisible = false;
  let projectVendorType = null;
  let roleId = null;

  if (inviteType === "ProjectUser") {
    dataSources = getProjectUserSources(data);
    nonCIQsrcPresent =
      dataSources.filter((src) => src !== SourceNames.CONSTRUCTIVIQ).length > 0;

    projectVendorType =
      data?.subscription_vendor &&
      data?.subscription_vendor?.project_vendors?.length &&
      data?.subscription_vendor?.project_vendors[0].project_vendors_type?.id;

    if (data?.project_role) {
      roleId = data?.project_role?.id;
    }
  } else {
    dataSources = getSubscriptionUserSources(data);
    nonCIQsrcPresent =
      dataSources.filter((src) => src !== SourceNames.CONSTRUCTIVIQ).length > 0;
    roleId = data?.subscription_permission_id;
  }
  isCIQUser = dataSources.includes(SourceNames.CONSTRUCTIVIQ);
  isInviteBtnVisible =
    nonCIQsrcPresent && data.status_id === AcceptanceStatus.NOT_INVITED;
  const organizations = data.subscription?.organization_subscriptions;
  const org = organizations?.length ? organizations[0] : null;
  const company = data?.subscription_vendor ?? org?.organization;

  const userData = {
    email: data?.user?.email,
    phone: data?.user?.phone,
    id: data?.user?.id,
    first_name: data?.user?.first_name,
    last_name: data?.user?.last_name,
    company_id: company?.id,
    company_name: company?.name,
    type: projectVendorType
  };

  return (
    <div className="flex space-x-3">
      <div className="w-12">
        <InviteStatusText
          statusId={value}
          userId={data?.user_id}
          inviteType={inviteType}
          isCIQUser={isCIQUser}
          isInviteBtnVisible={isInviteBtnVisible}
          context={context}
          userData={userData}
          roleId={roleId}
        />
      </div>
      {inviteType === "ProjectUser" &&
        isCIQUser &&
        value !== AcceptanceStatus.NOT_INVITED && (
          <div className="-mt-3">{projectUserActionRenderer(props)}</div>
        )}
    </div>
  );
}

export function userOrgCellRenderer(
  params: any,
  disableTooltip?: boolean
): any {
  const { data } = params;
  const userInput =
    params.userInput || params.colDef.cellRendererParams.userInput;

  if (!data[userInput.id] && !data[userInput.unregistered_user])
    return {
      cellMarkup: (
        <div className="flex justify-end">
          {agGridCheckFieldEditable(params) && <EditableCell />}
        </div>
      ),
      fullName: ""
    };

  let fullName = "";
  let userOrg = "";
  if (data[userInput.id]) {
    fullName = `${data[userInput.firstName]} ${data[userInput.lastName]}`;
    userOrg = data[userInput.org];
  }
  if (data[userInput.unregistered_user]) {
    fullName = data[userInput.unregistered_user];
    userOrg = data[userInput.unregistered_user_org];
  }

  const statusId = data[userInput.statusKey];

  let cellMarkup = <div />;
  try {
    cellMarkup = (
      <div className="flex w-full max-w-full h-full items-center">
        <div className="w-full max-w-full overflow-hidden space-y-1">
          <TruncatedTextWithTooltip
            className="w-full max-w-full leading-none colFirstValue"
            text={fullName}
            disableTooltip={disableTooltip}
            tooltipContent={
              <TitleDescriptionTooltip title={fullName} description={userOrg} />
            }
          />
          <TruncatedTextWithTooltip
            className="w-full max-w-full leading-none colSecondValue"
            text={userOrg}
            disableTooltip={disableTooltip}
            tooltipContent={
              <TitleDescriptionTooltip title={fullName} description={userOrg} />
            }
          />
        </div>
        {agGridCheckFieldEditable(params) && (
          <div className="flex items-center pl-1">
            <EditableCell />
          </div>
        )}
        {statusId && statusId === AcceptanceStatus.DEACTIVATED ? (
          <div className="absolute flex w-full items-start justify-end pr-3 -mt-[10px]">
            <UserTag label="Inactive" />
          </div>
        ) : (
          ""
        )}
      </div>
    );
  } catch {
    //
  }

  return {
    cellMarkup,
    fullName,
    userOrg
  };
}

function LinkedActivitiesTooltip(props: {
  activity: {
    activity_id: string;
    task_name: string;
    reverse_dependency: boolean;
    critical?: boolean;
    start_date?: string;
    progress: number;
    total_slack: number;
    linked_to_end_date?: boolean;
    end_date?: string;
    actual_start_date?: string;
    actual_finish_date?: string;
    total_slack_computed?: any;
    offset_duration?: any;
    linked_offset_date?: any;
  };
}) {
  const { activity } = props;
  const name = activity.activity_id
    ? `${activity.activity_id} - ${activity.task_name}`
    : activity.task_name;

  const getLabels = (label: string, value: string, valueClass: string = "") => {
    return (
      <div className="grid grid-cols-9 text-[10px] text-ciq-gray-440">
        <div className="col-span-4 font-medium">{label}</div>
        <div
          className={`flex items-center space-x-1 col-span-5 text-black text-opacity-80 ${valueClass}`}
        >
          <span>{value}</span>
          {activity?.linked_offset_date &&
            (label === "Planned Start date:" ||
              label === "Planned End date:") && (
              <ActivityOffsetTag task={activity} isShowDate />
            )}
        </div>
      </div>
    );
  };

  const floatVal = activity?.total_slack
    ? Math.floor((activity?.total_slack || 0) / 8)
    : "";
  const isPermissionEnabled = activity?.total_slack_computed !== "restricted";
  const duration =
    activity?.offset_duration < 0
      ? activity?.offset_duration
      : `+${activity?.offset_duration}`;
  const popupContent = (
    <div className="min-w-[228px] max-w-[280px]">
      {isPermissionEnabled && activity.critical && <CriticalActivityTag />}
      <div className="text-xs font-medium mt-2 mb-2">{name}</div>
      {activity.linked_to_end_date
        ? getLabels(
            "Planned End date:",
            DateUtils.format(activity?.end_date, "MM-DD-YYYY")
          )
        : getLabels(
            "Planned Start date:",
            DateUtils.format(activity?.start_date, "MM-DD-YYYY")
          )}
      {isPermissionEnabled &&
        getLabels(
          "Activity Float:",
          floatVal !== "" ? `${floatVal} days` : "",
          activity?.critical
            ? "font-bold text-black text-opacity-80"
            : "font-medium"
        )}
      {activity?.actual_start_date &&
        getLabels(
          "Actual Start date:",
          DateUtils.format(activity?.actual_start_date, "MM-DD-YYYY")
        )}
      {activity?.progress > 0 &&
        activity?.progress < 100 &&
        !activity?.actual_finish_date &&
        getLabels("Progress:", `${activity?.progress}%`)}
      {activity?.actual_finish_date &&
        getLabels(
          "Actual End date:",
          DateUtils.format(activity?.actual_finish_date, "MM-DD-YYYY")
        )}
      {activity.reverse_dependency && (
        <div className="mt-2">
          {getLabels("Link Properties:", "")}
          <div className="flex items-center justify-center bg-ciq-gray-500 rounded h-6 space-x-1 px-2">
            <span className="flex items-center">
              <SwapOutlined className="text-ciq-gray-550" />
            </span>
            <span className="flex items-center font-normal text-xs">
              Activity is the Predecessor
            </span>
          </div>
        </div>
      )}

      {activity.offset_duration && (
        <div className="mt-2">
          {getLabels("Link Properties:", "")}
          <div className="flex items-center justify-center bg-ciq-gray-500 rounded h-5 space-x-1 px-2">
            <span className="flex items-center">
              <OffsetIcon />
            </span>
            <span className="flex items-center font-normal text-xs">
              Offset: {duration} Days
            </span>
          </div>
        </div>
      )}
    </div>
  );

  const activityIcon = () => {
    if (
      isPermissionEnabled &&
      activity.critical &&
      activity.reverse_dependency
    ) {
      return (
        <span className="flex items-center">
          <span className="flex items-center bg-ciq-blue-300 p-1">
            <CriticalActivityIcon />
          </span>
          <span className="flex items-center p-1">
            <SwapOutlined className="text-ciq-gray-550" />
          </span>
        </span>
      );
    }
    if (isPermissionEnabled && activity.critical) {
      return (
        <span className="flex items-center bg-ciq-blue-400-alt-2 p-[3px] mr-1">
          <CriticalActivityIcon />
        </span>
      );
    }

    if (activity.reverse_dependency) {
      return (
        <span className="flex items-center p-1">
          <SwapOutlined className="text-ciq-gray-550" />
        </span>
      );
    }

    if (activity.linked_offset_date) {
      return (
        <span className="flex items-center p-1">
          <OffsetIcon />
        </span>
      );
    }
    return (
      <span className="flex items-center p-1">
        <LinkOutlined className="fill-link-outlined" />
      </span>
    );
  };

  return (
    <Popover trigger="hover" content={popupContent}>
      <Tag
        color="default"
        className={`flex w-[120px] space-x-0.5 items-center p-0 ${
          isPermissionEnabled && activity.critical
            ? "border-ciq-blue-300"
            : "border-gray-300"
        }`}
      >
        <span className="flex items-center">{activityIcon()}</span>
        <span className="truncate">{name}</span>
      </Tag>
    </Popover>
  );
}

export function TaskLinksCellRenderer(params: ICellRendererParams) {
  const { data, colDef } = params;
  const colId = colDef?.colId || colDef?.field;
  const tasks = sortAlphanumeric(data?.tasks || [], "activity_id");

  const toShow = tasks.slice(0, 2);
  const remaining = tasks.slice(2, tasks.length);

  const popupContent = (
    <div className="max-w-[260px] m-0 text-xs flex flex-wrap gap-y-1 gap-x-0">
      {remaining.map((task: any) => {
        return <LinkedActivitiesTooltip activity={task} key={task?.task_id} />;
      })}
    </div>
  );

  return (
    <div
      className="py-1 flex flex-wrap items-center gap-y-1 gap-x-0"
      data-testid={`${colId}`}
    >
      {toShow.map((task: any, index: number) => {
        return (
          <div className="flex items-center" key={task?.task_id}>
            <LinkedActivitiesTooltip activity={task} key={task?.task_id} />

            {index === 1 && tasks.length > 2 ? (
              <div className="flex-none flex-grow-0 leading-none">
                <Popover
                  content={popupContent}
                  title={<div className="py-1">Linked Task(s)</div>}
                >
                  <div>+{remaining.length}</div>
                </Popover>
              </div>
            ) : (
              ""
            )}
          </div>
        );
      })}
    </div>
  );
}

function LinkedMaterialTooltip(props: {
  material: {
    id: string;
    material_sequence_id: string;
    name: string;
    implicit: boolean;
    milestone_state: string;
    risk_level: string;
    spec_section: Array<{
      id: string;
      name: string;
      number: string;
    }>;
    predecessor_material?: boolean;
  };
}) {
  const { material } = props;

  const materialName = material.implicit
    ? material.name
    : `${material?.material_sequence_id} - ${material?.name}`;

  const row = (title: string, value: any, valueClassname = "") => (
    <div className="flex">
      <div className="w-12 text-ciq-gray-440">{title}</div>
      <div className={valueClassname}>{value}</div>
    </div>
  );
  const specSection = material?.spec_section?.length
    ? material?.spec_section[0]
    : null;

  const specSectionStr = specSectionLabel({
    specNumber: specSection?.number || "",
    specName: specSection?.name || ""
  });

  const popupContent = (
    <div className="min-w-[129px] max-w-[239px] text-black text-opacity-80">
      <div className="text-xs">{materialName}</div>
      <div className="text-[10px] pt-3 flex flex-col">
        <div className="flex-col">
          <div className="text-ciq-gray-440">Spec Section:</div>
          <div>{specSectionStr}</div>
        </div>
      </div>
      <div className="pt-3 flex flex-col text-[10px]">
        {row("Status:", material?.milestone_state)}
        {row(
          "Risk:",
          material?.risk_level,
          material?.risk_level === RiskLevelType.High
            ? "date-block-risk-high font-semibold"
            : ""
        )}
      </div>
      {material?.predecessor_material && (
        <div className="mt-2">
          <div className="text-[10px] text-ciq-gray-440 font-medium">
            Link Properties:
          </div>
          <div className="flex items-center justify-center bg-ciq-gray-500 rounded h-6 space-x-1 px-2">
            <span className="flex items-center">
              <SwapOutlined className="text-ciq-gray-550" />
            </span>
            <span className="flex items-center font-normal text-xs">
              Material is the Predecessor
            </span>
          </div>
        </div>
      )}
    </div>
  );
  return (
    <Popover trigger="hover" content={popupContent}>
      <Tag
        color="default"
        icon={
          material?.predecessor_material ? (
            <SwapOutlined className="text-ciq-gray-550" />
          ) : (
            <LinkOutlined className="fill-link-outlined" />
          )
        }
        className={
          material?.risk_level === RiskLevelType.High
            ? "w-[120px] truncate risk-high-box"
            : "bg-gray-100 w-[120px] truncate"
        }
      >
        {materialName}
      </Tag>
    </Popover>
  );
}

export function MaterialLinksCellRenderer(params: ICellRendererParams) {
  const { data } = params;
  const value = data?.materials?.filter((m: any) => !m.implicit);

  const materials = sortLinkedItems(value || [], "material_sequence_id");
  const actualMaterials = materials.filter(
    (material: any) => !material.implicit
  );
  const toShow = actualMaterials.slice(0, 2);
  const remaining = actualMaterials.slice(2, actualMaterials.length);
  const isHighEntityExist = remaining.some(
    (entity: any) => entity?.risk_level === RiskLevelType.High
  );

  const popupContent = (
    <div className="max-w-[260px] m-0 text-xs flex flex-wrap gap-y-1 gap-x-0">
      {remaining.map((item: any) => {
        return (
          <Link
            to={`materials/${item.id}`}
            target="_blank"
            data-testid="index-link-7"
            key={item.id}
          >
            <LinkedMaterialTooltip material={item} />
          </Link>
        );
      })}
    </div>
  );

  return (
    <div className="leading-none flex flex-wrap items-center gap-y-1 gap-x-0">
      {toShow.slice(0, 2).map((material: any, index: number) => {
        return (
          <div className="flex items-center" key={material.id}>
            <Link
              to={`materials/${material.id}`}
              target="_blank"
              data-testid="index-link-linked-material"
            >
              <LinkedMaterialTooltip material={material} />
            </Link>
            {index === 1 && actualMaterials.length > 2 ? (
              <div className="flex-none flex-grow-0">
                <Popover
                  content={popupContent}
                  title={<div className="py-1">Linked Material(s)</div>}
                >
                  <div
                    className={
                      isHighEntityExist
                        ? "date-block-risk-high font-semibold"
                        : ""
                    }
                  >
                    +{remaining.length}
                  </div>
                </Popover>
              </div>
            ) : (
              ""
            )}
          </div>
        );
      })}
    </div>
  );
}

function LinkedSubmittalTooltip(props: {
  showSpecSectionNo: boolean;
  submittal: {
    id: string;
    submittal_sequence_id: string;
    submittal_id?: string;
    name: string;
    implicit: boolean;
    risk_level: string;
    workflow_status: number;
    status: number;
    type: string;
    revision: number;
    spec_no: string;
    spec_section_no?: string;
    spec_name: string;
    deleted_from_source: boolean;
  };
}) {
  const { submittal, showSpecSectionNo } = props;
  const sequenceId =
    submittal?.submittal_sequence_id || submittal?.submittal_id || "";
  const specNo = submittal?.spec_no || submittal?.spec_section_no;

  const submittalRefrenceId: string =
    showSpecSectionNo && specNo ? `${specNo} - ${sequenceId}` : sequenceId;
  const name = `${submittalRefrenceId} - ${submittal?.name}`;
  const status = SubmittalStatusToStrMap[submittal?.workflow_status];
  const specSectionStr = specSectionLabel({
    specNumber: specNo,
    specName: submittal?.spec_name
  });
  const row = (title: string, value: any, valueClassname = "") => (
    <div className="flex">
      <div className="w-12 text-ciq-gray-440">{title}</div>
      <div className={valueClassname}>{value}</div>
    </div>
  );

  const popupContent = (
    <div className="min-w-[129px] max-w-[239px] text-black text-opacity-80">
      {submittal.workflow_status === ESubmittalStatus.VOID && (
        <div className="pb-3">
          <div className="w-full p-1 bg-ciq-void-100 text-black text-opacity-50 flex items-center justify-center font-bold text-xs rounded">
            Void
          </div>
        </div>
      )}
      {submittal?.deleted_from_source && (
        <div className="pb-3">
          <div className="w-full p-1 bg-ciq-void-100 text-black text-opacity-50 flex items-center justify-center font-bold text-xs rounded">
            Deleted
          </div>
        </div>
      )}
      <div className="text-xs">{name}</div>
      <div className="text-[10px] pt-3 flex flex-col">
        <div className="flex-col">
          <div className="text-ciq-gray-440">Spec Section:</div>
          <div>{specSectionStr}</div>
        </div>
      </div>

      <div className="pt-3 flex flex-col text-[10px]">
        {row("Type:", submittal?.type)} {row("Revision:", submittal?.revision)}
      </div>
      <div className="pt-3 flex flex-col text-[10px]">
        {row("Status:", status)}{" "}
        {row(
          "Risk:",
          submittal?.deleted_from_source ? "" : submittal?.risk_level,
          submittal?.risk_level === RiskLevelType.High
            ? "date-block-risk-high font-semibold"
            : ""
        )}
      </div>
    </div>
  );
  const getClass = () => {
    switch (true) {
      case submittal?.deleted_from_source:
        return "w-[120px] truncate bg-ciq-blue-50 border-ciq-void-200";
      case submittal.workflow_status === ESubmittalStatus.VOID:
        return "w-[120px] truncate bg-ciq-void-100 border-ciq-void-200";
        break;
      case submittal?.risk_level === RiskLevelType.High:
        return "w-[120px] truncate risk-high-box";
      default:
        return "bg-gray-100 w-[120px] truncate";
        break;
    }
  };
  return (
    <Popover trigger="hover" content={popupContent}>
      {submittal?.deleted_from_source ? (
        <Tag color="default" className={getClass()}>
          <span className="text-black text-opacity-50 font-bold text-xs mr-2">
            Deleted
          </span>
          <span className="text-black text-opacity-30 font-normal text-xs">
            {name}
          </span>
        </Tag>
      ) : (
        <Tag
          color="default"
          icon={
            submittal.workflow_status === ESubmittalStatus.VOID ? (
              <VoidSubmittalIcon type="text" />
            ) : (
              <LinkOutlined className="fill-link-outlined" />
            )
          }
          className={getClass()}
        >
          {name}
        </Tag>
      )}
    </Popover>
  );
}

type TLinkedSubmittal = {
  id: string;
  feature_type: number;
  sequence_id: number;
  name: string;
  milestone_state: string;
  risk_level?: RiskLevelType;
  workflow_status: EWorkflowStatusDataBlock;
  deleted_from_source: boolean;
  type: any;
  revision?: number;
  spec_section: Array<{
    id: string;
    name: string | null;
    number: string | null;
  }>;
  milestones: Array<{
    actual_date: string | null;
    name: string | null;
    projected_date: string | null;
    planned_date: string | null;
    sequence_no: number;
    milestone_action: string | null;
  }>;
};

function LinkedSubmittalEntityWithPopover(props: {
  showSpecSectionNo: boolean;
  submittal: TLinkedSubmittal;
}) {
  const { submittal, showSpecSectionNo } = props;

  const sequenceId = submittal?.sequence_id || "";
  const specSection = submittal.spec_section?.length
    ? submittal.spec_section[0]
    : null;
  const specSectionStr = specSectionLabel({
    specNumber: specSection?.number,
    specName: specSection?.name
  });

  const submittalRefrenceId: string =
    showSpecSectionNo && specSection
      ? `${specSection.number} - ${sequenceId}`
      : `${sequenceId}`;
  const name = `${submittalRefrenceId} - ${submittal?.name}`;
  const status = submittal.milestone_state;

  const submittalWFStarted = useMemo(() => {
    return (
      submittal?.workflow_status === EWorkflowStatusDataBlock.IN_PROGRESS ||
      submittal?.workflow_status === EWorkflowStatusDataBlock.WF_COMPLETED
    );
  }, [submittal]);

  const isVoidSubmittal = useMemo(
    () => submittal.workflow_status === EWorkflowStatusDataBlock.VOID,
    [submittal]
  );

  const isDeletedSubmittal = useMemo(
    () => submittal.deleted_from_source,
    [submittal]
  );

  const submittalWFStatusUsingDateBlock = useMemo(() => {
    const subcontractorMilestone = submittal.milestones?.find(
      (x) => x.sequence_no === 2
    );
    const designReviewMilestone = submittal.milestones?.find(
      (x) => x.sequence_no === 4
    );

    const submitDate =
      subcontractorMilestone?.actual_date ||
      (submittalWFStarted
        ? subcontractorMilestone?.projected_date
        : subcontractorMilestone?.planned_date);

    const designReviewDate =
      designReviewMilestone?.actual_date ||
      (submittalWFStarted
        ? designReviewMilestone?.projected_date
        : designReviewMilestone?.planned_date);

    return {
      submit_by_subcontractor: {
        label: subcontractorMilestone?.name || "",
        date: submitDate ? DateUtils.format(submitDate, "MM-DD-YY") : "",
        milestoneCompleted: !!subcontractorMilestone?.actual_date
      },
      design_review_complete: {
        label: designReviewMilestone?.name || "",
        date: designReviewDate
          ? DateUtils.format(designReviewDate, "MM-DD-YY")
          : "",
        milestoneCompleted: !!designReviewMilestone?.actual_date
      }
    };
  }, [submittal, submittalWFStarted]);

  const row = useCallback(
    (title: string, value: any, valueClassname = "") => (
      <div className="flex">
        <div className="w-12 text-ciq-gray-440">{title}</div>
        <div className={valueClassname}>{value}</div>
      </div>
    ),
    []
  );

  const getStatusIcon = useCallback(
    (dateStr: string, milestoneCompleted: boolean) => {
      if (dateStr) {
        return milestoneCompleted ? <TickIcon /> : <ClockIcon />;
      }
      return "";
    },
    []
  );

  const wfStatusRow = useCallback(
    (
      title: string = "",
      value: any = "",
      milestoneCompleted: boolean = false
    ) => (
      <>
        <div className="text-ciq-gray-440">{title}:</div>
        <div className={value ? "text-right" : "text-left"}>{value || "-"}</div>
        <div className="w-[10px] flex justify-center items-center">
          {getStatusIcon(value, milestoneCompleted)}
        </div>
      </>
    ),
    [getStatusIcon]
  );

  const popupContent = (
    <div className="text-black text-opacity-80 max-w-[239px]">
      {isVoidSubmittal && (
        <div className="pb-3">
          <div className="w-full p-1 bg-ciq-void-100 text-black text-opacity-50 flex items-center justify-center font-bold text-xs rounded">
            Void
          </div>
        </div>
      )}

      {isDeletedSubmittal && (
        <div className="w-full pb-2">
          <DeletedSubmittalTag />
        </div>
      )}
      <div className="text-xs font-medium mb-3">
        {submittal?.sequence_id} - {submittal?.name}
      </div>
      <div className="text-[10px] space-y-3">
        <div>
          <div className="text-ciq-gray-440">Spec Section:</div>
          <div>{specSectionStr}</div>
        </div>
        <div>
          {row("Type:", submittal?.type)}
          {row("Revision:", submittal?.revision)}
        </div>
        {!isVoidSubmittal && !isDeletedSubmittal && (
          <>
            <div>
              {row("Status:", status)}
              {row(
                "Risk:",
                submittal?.risk_level,
                submittal?.risk_level === RiskLevelType.High
                  ? "date-block-risk-high font-semibold"
                  : ""
              )}
            </div>
            <div>
              <div className="grid grid-cols-[max-content_auto_12px] gap-x-1">
                {wfStatusRow(
                  submittalWFStatusUsingDateBlock?.submit_by_subcontractor
                    ?.label,
                  submittalWFStatusUsingDateBlock?.submit_by_subcontractor
                    ?.date,
                  submittalWFStatusUsingDateBlock?.submit_by_subcontractor
                    ?.milestoneCompleted
                )}

                {wfStatusRow(
                  submittalWFStatusUsingDateBlock?.design_review_complete
                    ?.label,
                  submittalWFStatusUsingDateBlock?.design_review_complete?.date,
                  submittalWFStatusUsingDateBlock?.design_review_complete
                    ?.milestoneCompleted
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );

  const conditionalClasses = useMemo(() => {
    switch (true) {
      case isVoidSubmittal:
        return "bg-ciq-gray-400-alt-2 border-ciq-void-200";
      case isDeletedSubmittal:
        return "bg-ciq-gray-400-alt-2 border-ciq-void-200";
      case submittal?.risk_level === RiskLevelType.High:
        return "risk-high-box";
      default:
        return "bg-ciq-gray-360 border-black border-opacity-10";
    }
  }, [isVoidSubmittal, isDeletedSubmittal, submittal?.risk_level]);

  const linkIcon = useMemo(() => {
    return isVoidSubmittal ? (
      <VoidSubmittalIcon type="text" />
    ) : (
      <LinkOutlined />
    );
  }, [isVoidSubmittal]);

  return (
    <Popover trigger="hover" content={popupContent}>
      {isDeletedSubmittal ? (
        <Tag
          color="default"
          className={`flex rounded-xs items-center w-full max-w-full m-0 py-[1.5px] ${conditionalClasses}`}
        >
          <div className="w-full max-w-full py-0.5 mb-0.5 overflow-hidden leading-normal text-wrap whitespace-normal break-words">
            <span className="mr-1 align-middle text-black text-opacity-50 font-bold text-xs">
              Deleted
            </span>
            <span className="text-xs text-wrap font-normal text-black text-opacity-50">
              {name}
            </span>
          </div>
        </Tag>
      ) : (
        <Tag
          color="default"
          className={`w-full max-w-full m-0 py-[1.5px] ${conditionalClasses}`}
        >
          {!isVoidSubmittal ? (
            <>
              <div className="w-full max-w-full overflow-hidden truncate leading-none py-0.5 mb-0.5">
                <span className="mr-1 align-middle">{linkIcon}</span>
                <span className="text-xs text-wrap font-semibold align-middle">
                  {name}
                </span>
              </div>
              <div className="space-y-0.5">
                <div className="text-xs align-middle leading-none flex">
                  <div className="w-[60px]">Submitted:</div>
                  <div
                    className={`grow ${
                      submittalWFStatusUsingDateBlock?.submit_by_subcontractor
                        ?.date
                        ? "text-right"
                        : "pl-1.5"
                    }`}
                  >
                    {submittalWFStatusUsingDateBlock?.submit_by_subcontractor
                      ?.date || "-"}
                  </div>
                  <div className="pl-1 min-w-[10px]">
                    {getStatusIcon(
                      submittalWFStatusUsingDateBlock?.submit_by_subcontractor
                        ?.date,
                      submittalWFStatusUsingDateBlock?.submit_by_subcontractor
                        ?.milestoneCompleted
                    )}
                  </div>
                </div>

                <div className="text-xs align-middle leading-none flex">
                  <div className="w-[60px]">Approved:</div>
                  <div
                    className={`grow ${
                      submittalWFStatusUsingDateBlock?.design_review_complete
                        ?.date
                        ? "text-right"
                        : "pl-1.5"
                    }`}
                  >
                    {submittalWFStatusUsingDateBlock?.design_review_complete
                      ?.date || "-"}
                  </div>
                  <div className="pl-1 min-w-[10px]">
                    {getStatusIcon(
                      submittalWFStatusUsingDateBlock?.design_review_complete
                        ?.date,
                      submittalWFStatusUsingDateBlock?.design_review_complete
                        ?.milestoneCompleted
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="w-full max-w-full py-0.5 mb-0.5 overflow-hidden leading-normal text-wrap whitespace-normal break-words">
              <span className="mr-1 align-middle">
                {isVoidSubmittal && <VoidSubmittalIcon type="text" />}
              </span>
              <span className="text-xs text-wrap font-semibold">{name}</span>
            </div>
          )}
        </Tag>
      )}
    </Popover>
  );
}

const sortFeatureSubmittalLinkedItems = (items: Array<TLinkedSubmittal>) => {
  const riskOrder = {
    HIGH: 3,
    MEDIUM: 2,
    LOW: 1,
    blank: 0
  };
  return items.sort((a, b) => {
    const aVoid = a.workflow_status === EWorkflowStatusDataBlock.VOID;
    const bVoid = b.workflow_status === EWorkflowStatusDataBlock.VOID;

    // 1. Priority: VOID first
    if (aVoid && !bVoid) return -1;
    if (!aVoid && bVoid) return 1;

    // 2. Priority: risk_level (HIGH > MEDIUM > LOW > blank)
    const riskDiff =
      riskOrder[b.risk_level ?? "blank"] - riskOrder[a.risk_level ?? "blank"];
    if (riskDiff !== 0) return riskDiff;

    // 3. Priority: sequence_id ascending
    return a.sequence_id - b.sequence_id;
  });
};

export function SubmittalLinksEntityCellRenderer(params: ICellRendererParams) {
  const { data, context } = params;
  const showSpecSectionNo = context.projectDetails?.spec_section;

  const linkedItems = (data?.linked_items || []) as Array<TLinkedSubmittal>;

  const submittals = sortFeatureSubmittalLinkedItems(
    linkedItems?.filter((x: TLinkedSubmittal) => x.feature_type === 1)
  );

  const toShow = submittals.slice(0, 2);
  const remaining = submittals.slice(2, submittals.length);

  const isHighEntityExist = remaining.some(
    (entity: any) => entity?.risk_level === RiskLevelType.High
  );

  const popupContent = (
    <div className="m-0 text-xs grid grid-cols-2 gap-y-2 gap-x-2">
      {remaining.map((item: any) => {
        return (
          <div key={item.id}>
            <Link
              target="_blank"
              to={`submittals/${item.id}`}
              className="w-[160px] h-[56px] flex items-stretch"
              data-testid="index-link-linked-submittal-entity-with-popover"
            >
              <LinkedSubmittalEntityWithPopover
                submittal={item}
                showSpecSectionNo={showSpecSectionNo}
              />
            </Link>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="h-full py-0.5 flex items-stretch gap-x-2">
      {toShow.map((submittal: any) => {
        return (
          <div className="flex items-stretch space-x-2" key={submittal.id}>
            <Link
              target="_blank"
              to={`submittals/${submittal.id}`}
              className="w-[160px] leading-normal flex items-stretch"
              data-testid="index-link-linked-submittal-entity-with-popover-2"
            >
              <LinkedSubmittalEntityWithPopover
                submittal={submittal}
                showSpecSectionNo={showSpecSectionNo}
              />
            </Link>
          </div>
        );
      })}
      {submittals.length > 2 ? (
        <div className="flex-none flex-grow-0 leading-none flex items-center">
          <Popover
            content={popupContent}
            title={<div className="py-1">Linked Submittal(s)</div>}
          >
            <div
              className={
                isHighEntityExist ? "date-block-risk-high font-semibold" : ""
              }
            >
              +{remaining.length}
            </div>
          </Popover>
        </div>
      ) : (
        ""
      )}
    </div>
  );
}

export const responsibleContractorCellRenderer = (params: any): any => {
  const { data } = params;
  let cellMarkup = <div />;

  try {
    cellMarkup = (
      <div className="w-full flex items-center">
        <TruncatedTextWithTooltip
          text={data.responsible_contractor_name || data.trade_partner_name}
        />
        <div className="px-1">
          <EditOutlined className="cell-edit-icon" />
        </div>
      </div>
    );
  } catch (ex) {
    return "";
  }

  return {
    cellMarkup,
    orgName: data?.responsible_contractor_name || data.trade_partner_name || ""
  };
};

export function gcRepresentativeCellRendererWithCustomEdit(params: any): any {
  const { data, context } = params;
  const onClickEdit = () => {
    context.onEditCell({
      rowIndex: params.node.rowIndex!,
      colKey: params.column!.getId(),
      data
    });
  };
  const userInput =
    params.userInput || params.colDef.cellRendererParams.userInput;
  if (!data[userInput.id])
    return {
      cellMarkup: (
        <button
          type="button"
          className="flex justify-end w-full border-0 bg-transparent"
          onClick={onClickEdit}
          data-testid="index-button-gc-representative"
        >
          {agGridCheckFieldEditable(params) && <EditableCell />}
        </button>
      ),
      fullName: ""
    };

  let fullName = "";
  if (data[userInput.id]) {
    fullName = `${data[userInput.firstName]} ${data[userInput.lastName]}`;
  }

  const statusId = data[userInput.statusKey];

  let cellMarkup = <div />;
  try {
    cellMarkup = (
      <button
        type="button"
        className="w-full flex border-0 bg-transparent text-left items-center"
        onClick={onClickEdit}
        data-testid="index-button-gc-representative-custom-edit"
      >
        {statusId && statusId === AcceptanceStatus.DEACTIVATED ? (
          <div className="absolute flex w-full items-start justify-end pr-4 -top-2.5">
            <UserTag label="Inactive" />
          </div>
        ) : (
          ""
        )}
        <div className="grow overflow-hidden">
          <TruncatedTextWithTooltip text={fullName} />
        </div>
        <div>{agGridCheckFieldEditable(params) && <EditableCell />}</div>
      </button>
    );
  } catch {
    //
  }

  return {
    cellMarkup,
    fullName
  };
}

export function gcRepresentativeCellRenderer(params: any): any {
  const { data } = params;
  const userInput =
    params.userInput || params.colDef.cellRendererParams.userInput;
  if (!data[userInput.id])
    return {
      cellMarkup: (
        <div className="flex justify-end">
          {agGridCheckFieldEditable(params) && <EditableCell />}
        </div>
      ),
      fullName: ""
    };

  let fullName = "";
  if (data[userInput.id]) {
    fullName = `${data[userInput.firstName]} ${data[userInput.lastName]}`;
  }

  let cellMarkup = <div />;
  try {
    cellMarkup = (
      <div className="flex items-start">
        <div>
          <p className="colFirstValue">{fullName}</p>
        </div>
        {agGridCheckFieldEditable(params) && <EditableCell />}
      </div>
    );
  } catch {
    //
  }

  return {
    cellMarkup,
    fullName
  };
}

export function SubmittalReportDetailsCellRenderer(params: any) {
  const { data } = params;
  const submittalData = { ...data };

  submittalData.submittal_schedule_links =
    submittalData.submittal_schedule_links || [];
  submittalData.submittal_material_links =
    submittalData.submittal_material_links?.map((m: any) => ({
      assignee: m.assignee,
      driving_material: m.driving_material,
      material_id: m.id,
      material_sequence_id: m.material_id,
      material_name: m.name,
      status: m.status,
      implicit: m.implicit,
      material: {
        date_block_materials: [
          {
            planned_milestone_1: m.ROJ_date,
            name_milestone_1: m.last_milestone_name
          }
        ]
      }
    })) || [];

  const governingActivity = useMemo(() => {
    const linkedTask = submittalData?.submittal_schedule_links?.find(
      (task: any) => task.driving_task
    );
    if (linkedTask)
      return {
        ...linkedTask.gantt_task,
        text: linkedTask.task_name,
        isLinkedEndDate: linkedTask.linked_to_end_date
      };

    return {} as any;
  }, [submittalData?.submittal_schedule_links]);

  return (
    <div>
      <DateBlockSubmittalImpactVisualisation
        submittal={submittalData}
        governingTask={governingActivity}
      />
    </div>
  );
}

export function UpcomingSubmittalReportDetailsCellRenderer(params: any) {
  const { data } = params;
  const submittalData = { ...data };
  const { projectId } = useParams() as any;

  submittalData.submittal_schedule_links =
    submittalData.submittal_schedule_links || [];
  submittalData.submittal_material_links =
    submittalData.submittal_material_links?.map((m: any) => ({
      assignee: m.assignee,
      driving_material: m.driving_material,
      material_id: m.id,
      material_sequence_id: m.material_id,
      material_name: m.name,
      status: m.status,
      implicit: m.implicit,
      material: {
        date_block_materials: [
          {
            planned_milestone_1: m.ROJ_date,
            name_milestone_1: m.last_milestone_name
          }
        ]
      },

      risk_level: m.risk_level,
      milestone_state: m.milestone_state,
      spec_section_name: m.spec_section_name,
      spec_section_no: m.spec_section_no,
      spec_section: [
        {
          id: m?.spec_section_id,
          name: m?.spec_section_name,
          number: m?.spec_section_no
        }
      ]
    })) || [];

  const governingActivity = useMemo(() => {
    const linkedTask = submittalData?.submittal_schedule_links?.find(
      (task: any) => task.driving_task
    );
    if (linkedTask)
      return {
        ...linkedTask.gantt_task,
        text: linkedTask.task_name,
        isLinkedEndDate: linkedTask.linked_to_end_date
      };

    return {} as any;
  }, [submittalData?.submittal_schedule_links]);

  return (
    <div className="flex flex-col w-[98%]">
      <div className="flex font-medium space-x-2 pt-3 pl-10">
        <div className="px-2">Impacted Materials:</div>
        <div className="flex flex-wrap gap-2">
          {submittalData?.submittal_material_links?.length ? "" : "None"}
          {submittalData?.submittal_material_links?.map((item: any) => {
            let materialName = "";
            if (item.implicit) {
              materialName = `Material for Submittal ${submittalData.submittal_id}`;
            } else {
              materialName = item.material_name;
            }
            const tag = (
              <LinkedMaterialTooltip
                material={{ ...item, name: materialName }}
              />
            );
            return item.implicit ? (
              tag
            ) : (
              <Link
                to={`/project/${projectId}/materials/${item.material_id}`}
                target="_blank"
                key={materialName}
                data-testid="index-link-upcoming-submittal-report"
              >
                {tag}
              </Link>
            );
          })}
        </div>
      </div>

      <div className="flex font-medium space-x-2 pt-4 pl-10">
        <div className="px-2">Impacted Activities:</div>
        <div className="flex flex-wrap gap-2">
          {submittalData?.submittal_schedule_links?.length ? "" : "None"}
          {submittalData?.submittal_schedule_links?.map((item: any) => {
            return <LinkedActivitiesTooltip activity={item} key={item.id} />;
          })}
        </div>
      </div>
      <DateBlockSubmittalImpactVisualisation
        submittal={submittalData}
        governingTask={governingActivity}
      />
    </div>
  );
}

export function UpcomingMaterialReportDetailsCellRenderer(params: any) {
  const { data, context } = params;
  const { projectId } = useParams() as any;
  const materialData = { ...data };
  const showSpecSectionNo = context.projectDetails?.spec_section;

  materialData.material_material_links =
    materialData?.material_material_links?.map((item: any) => {
      const specSection = {
        id: item?.spec_section?.spec_section_id,
        name: item?.spec_section?.spec_section_name,
        number: item?.spec_section?.spec_section_no
      };
      return {
        ...item,
        spec_section: [specSection]
      };
    });

  return (
    <div className="flex flex-col w-[98%] pb-3">
      <div className="flex font-medium space-x-2 pt-3 pl-10">
        <div className="px-2">Linked Submittals:</div>
        <div className="flex flex-wrap gap-2">
          {materialData?.material_submitttal_links?.length ? "" : "None"}
          {materialData?.material_submitttal_links?.map((item: any) => {
            return (
              <Link
                to={`/project/${projectId}/submittals/${item.id}`}
                target="_blank"
                className="leading-normal"
                data-testid="index-link-upcoming-material-report"
              >
                <LinkedSubmittalTooltip
                  submittal={item}
                  showSpecSectionNo={showSpecSectionNo}
                />
              </Link>
            );
          })}
        </div>
      </div>
      <div className="flex font-medium space-x-2 pt-3 pl-10">
        <div className="px-2">Impacted Materials:</div>
        <div className="flex flex-wrap gap-2">
          {materialData?.material_material_links?.length ? "" : "None"}
          {materialData?.material_material_links?.map((item: any) => {
            let materialName = "";
            if (item.implicit) {
              materialName = `Material for Submittal ${materialData.submittal_id}`;
            } else {
              materialName = item.material_name || item.name;
            }
            const tag = (
              <LinkedMaterialTooltip
                material={{ ...item, name: materialName }}
              />
            );
            return item.implicit ? (
              tag
            ) : (
              <Link
                to={`/project/${projectId}/materials/${item.material_id}`}
                target="_blank"
                key={materialName}
                data-testid="index-link-upcoming-submittal-report"
              >
                {tag}
              </Link>
            );
          })}
        </div>
      </div>

      <div className="flex font-medium space-x-2 pt-4 pl-10">
        <div className="px-2">Impacted Activities:</div>
        <div className="flex flex-wrap gap-2">
          {materialData?.material_schedule_links?.length ? "" : "None"}
          {materialData?.material_schedule_links?.map((item: any) => {
            return <LinkedActivitiesTooltip activity={item} key={item.id} />;
          })}
        </div>
      </div>
    </div>
  );
}

export const nextDeadlineCellRenderer = (
  params: any,
  disableTooltip: boolean
) => {
  if (!params.value) return null;
  const colId = params.colDef?.colId || params.colDef?.field;
  const todayStartDateObject = DateUtils.dateTimeObj().startOf("day");
  const nextDeadlineDateObject = DateUtils.dateTimeObj(params.value);
  const isPast = nextDeadlineDateObject.isBefore(todayStartDateObject);
  const cellValueClassnames = isPast ? "date-block-date-risk" : "";
  return (
    <div className="flex items-center">
      <TruncatedTextWithTooltip
        text={params.value}
        className={cellValueClassnames}
        disableTooltip={disableTooltip}
        dataTestId={colId}
      />
    </div>
  );
};

export const featureTemplateListNameCellRenderer = (params: any) => {
  const { data, context } = params;

  return (
    <div className="w-full">
      <div className="flex space-x-3 min-w-0">
        <div className="shrink overflow-hidden text-ellipsis">
          <button
            type="button"
            data-testid="index-button-feature-template-name"
            className="bg-transparent border-0 underlined-text cursor-pointer whitespace-pre"
            onClick={() => {
              context.onTemplateNameClick(data);
            }}
          >
            {params.value}
          </button>
        </div>
        {data.default && (
          <div>
            <DefaultTag />
          </div>
        )}
      </div>
    </div>
  );
};

export function FeatureTemplateListActionsCellRenderer(params: any) {
  const {
    data,
    context,
    savingDefaultTemplate,
    canEditFeatureDBTemplate,
    featureTemplates
  } = params;
  const { onWorkflowCopy } = context;
  const [copyPopupVisible, setCopyPopupVisible] = useState(false);

  const isTemplateDisabled = data.disabled;
  const isTemplateUsed =
    !!data.feature_workflow_instances_aggregate?.aggregate?.count;

  const availableTemplateNames = useMemo(() => {
    return featureTemplates.map((template: any) => {
      return template.name.toLowerCase();
    });
  }, [featureTemplates]);

  const editDisableTooltip = useMemo(() => {
    if (!canEditFeatureDBTemplate) {
      return ErrorMessages.PermissionNotGranted;
    }

    return "";
  }, [canEditFeatureDBTemplate]);

  const isCopyDisabled =
    savingDefaultTemplate || !canEditFeatureDBTemplate || isTemplateDisabled;

  return (
    <div className="flex items-center">
      <Button
        disabled={
          savingDefaultTemplate ||
          !canEditFeatureDBTemplate ||
          isTemplateDisabled
        }
        title={editDisableTooltip}
        type="text"
        size="small"
        className="-mt-0.5 border-0"
        onClick={() => {
          context.onTemplateEditClick(data);
        }}
        data-testid="index-button-feature-template-list-actions"
      >
        <EditOutlined />
      </Button>
      {!isCopyDisabled ? (
        <Popover
          open={copyPopupVisible}
          placement="bottom"
          content={
            <CopyDBTemplate
              setCopyPopupVisible={setCopyPopupVisible}
              availableTemplateNames={availableTemplateNames}
              template={data}
              onCopy={onWorkflowCopy}
            />
          }
          trigger="click"
          destroyTooltipOnHide
          onOpenChange={() => {
            setCopyPopupVisible(!copyPopupVisible);
          }}
        >
          <Button
            type="text"
            size="small"
            className="pt-0.5 border-0"
            onClick={() => {
              setCopyPopupVisible(true);
            }}
            data-testid="index-button-copy"
          >
            <CopyIcon />
          </Button>
        </Popover>
      ) : (
        <Button
          disabled
          type="text"
          size="small"
          className="pt-0.5 border-0"
          title={
            !canEditFeatureDBTemplate ? ErrorMessages.PermissionNotGranted : ""
          }
        >
          <CopyIcon />
        </Button>
      )}

      <Switch
        className="-mt-0.5 ml-1.5"
        checked={!isTemplateDisabled}
        data-testid="index-switch-template-enabled"
        onChange={(value: any) => {
          context.onToggleTemplateDisabledConfirm(data, value);
        }}
        size="small"
        disabled={!canEditFeatureDBTemplate || savingDefaultTemplate}
        title={
          !canEditFeatureDBTemplate ? ErrorMessages.PermissionNotGranted : ""
        }
      />
      {isTemplateUsed && (
        <div className="ml-2">
          <Tag className="whitespace-pre-line break-words leading-tight mt-[10px]">
            Template in use. Actions limited.
          </Tag>
        </div>
      )}
    </div>
  );
}

export const featureTemplateListDefaultCellRenderer = (params: any) => {
  const { data, context, savingDefaultTemplate, canEditFeatureDBTemplate } =
    params;

  const isTemplateDisabled = data.disabled;

  return (
    <div className="flex items-center">
      <Radio.Group
        disabled={
          savingDefaultTemplate ||
          !canEditFeatureDBTemplate ||
          isTemplateDisabled
        }
        onChange={() => {
          context.onTemplateDefaultChange(data);
        }}
        value={data.default ? 1 : 0}
      >
        <Radio className="ml-5" value={1} />
      </Radio.Group>
    </div>
  );
};

export const integrationTypeCellRenderer = (params: any) => {
  const { value } = params;
  const integrationSystemName = IntegrationSystemNameMap[value];

  const sourceLogo = () => {
    switch (value) {
      case IntegrationType[IntegrationType.PROCORE]:
        return <ProcoreLogoIcon />;
      case IntegrationType[IntegrationType.AUTODESK]:
        return <AutodeskLogoIcon />;
      case IntegrationType[IntegrationType.BIM360]:
        return (
          <img src={BIM360logo} alt="" style={{ width: 18, height: 19 }} />
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex items-center space-x-2" title={integrationSystemName}>
      <div className="flex items-center">{sourceLogo()}</div>
      <TruncatedTextWithTooltip text={integrationSystemName} />
    </div>
  );
};

export function IntegrationNameCellRenderer(params: any) {
  const { context, data } = params;

  const isFileBasedIntegration =
    data?.systemIntgConfigProperties?.INTEGRATION_METHOD === "file";

  const subReauthRequired =
    data.properties.SUBSCRIPTION_CONFIG_AUTH_STATE &&
    data.properties.SUBSCRIPTION_CONFIG_AUTH_STATE ===
      IntegrationAuthStates.REAUTHORIZATION_REQUIRED;

  const projReauthRequired =
    data.properties.PROJECT_CONFIG_AUTH_STATE &&
    data.properties.PROJECT_CONFIG_AUTH_STATE ===
      IntegrationAuthStates.REAUTHORIZATION_REQUIRED;

  const reauthRequired = subReauthRequired || projReauthRequired;

  return (
    <div className="max-w-full flex items-center">
      <div className="grow truncate flex items-center">
        {!isFileBasedIntegration ? (
          <button
            type="button"
            className="gridButtonLink "
            onClick={async () => {
              context.onManageIntegrationClick(data);
            }}
            data-testid="index-button-integration-name"
          >
            <div className="space-x-1">
              <span>{data.properties.INTEGRATION_NAME}</span>
            </div>
          </button>
        ) : (
          <TruncatedTextWithTooltip text={data.properties.INTEGRATION_NAME} />
        )}
      </div>
      <div className="shrink-0 flex items-end">
        {reauthRequired && (
          <div className=" mt-2">
            <Popover
              placement="left"
              content={
                <div className="w-[300px]">
                  Authorization has expired and integration with{" "}
                  {`${IntegrationSystemNameMap[data.integration.system]}`} is no
                  longer valid. View configuration details to renew the
                  connection.
                </div>
              }
            >
              <div className="inline">
                <AuthenticationExpiredIcon />
              </div>
            </Popover>
          </div>
        )}
      </div>
    </div>
  );
}

export const dlGrpUserNameCellRenderer = (params: any): any => {
  const { data } = params;

  let cellDiv = <div />;
  try {
    cellDiv = (
      <div className="flex">
        <TruncatedTextWithTooltip text={data.name} />
        {data.status_id && data.status_id === AcceptanceStatus.DEACTIVATED ? (
          <div className="absolute flex w-full items-start justify-end pr-3 -top-2">
            <UserTag label="Inactive" />
          </div>
        ) : (
          ""
        )}
      </div>
    );
  } catch {
    //
  }

  return cellDiv;
};

export const userNameCellRenderer = (params: any) => {
  const { data } = params;
  const userInfo = data?.created_by_user ?? data?.user_created;

  const fullName = `${userInfo?.first_name ?? ""} ${userInfo?.last_name ?? ""}`;
  const email = userInfo?.email ?? "";

  const userDetails = (
    <div className="flex flex-col ">
      <div className="flex items-center h-5">{fullName}</div>
      <div className="flex items-center h-5">{email}</div>
    </div>
  );

  return userDetails;
};

export const riskCellRenderer = (
  { value, data, colDef }: any,
  disableTooltip: boolean
) => {
  if (!value) return "";
  if (data?.workflow_status === ESubmittalStatus.VOID) return "";
  const colId = colDef?.colId || colDef?.field;

  let cssColor = "";
  switch (value) {
    case RiskLevelType.High:
      cssColor = "bg-ciq-red-100 border-ciq-red-300 text-ciq-red-700";
      break;
    case RiskLevelType.Medium:
      cssColor = "bg-ciq-orange-100 border-ciq-orange-300 text-ciq-orange-700";
      break;
    default:
      cssColor = "bg-ciq-blue-100 border-ciq-blue-300 text-ciq-blue-700";
      break;
  }

  return (
    <div className="text-xs flex flex-wrap items-start h-full p-1">
      <div
        className={`border border-solid capitalize rounded px-2 py-1 ${cssColor}`}
      >
        <TruncatedTextWithTooltip
          text={(value || "").toLowerCase()}
          disableTooltip={disableTooltip}
          dataTestId={colId}
        />
      </div>
    </div>
  );
};

export const materialActionCellRenderer = (params: any) => {
  const { context, data } = params;
  const {
    history,
    projectDetails,
    onLinkClick,
    onRequestDurationClick,
    canRequestLeadTime,
    isRequestLeadTimeFeatureEnabled,
    isMaterialExtractionEnabled,
    canInlineEditMaterialGeneralInfoAndParticipants,
    onCommentClick
  } = context;

  const isAssigneePresent = data?.assignee_user;
  const isAssigneeUnregistered = !data?.assignee_user?.email;

  const requestAlreadySent =
    data?.invite_status &&
    data?.invite_status === EmailInviteStatus.REQUEST_SENT;

  const assigneeIsInactive = data?.assignee_user?.id
    ? data?.assignee_user?.status === AcceptanceStatus.DEACTIVATED
    : false;

  const requestBtnDisabled =
    !isAssigneePresent ||
    assigneeIsInactive ||
    isAssigneeUnregistered ||
    requestAlreadySent;

  const linkButton = (
    <button
      type="button"
      className="ciq-icon-button !p-1.5"
      onClick={() => {
        onLinkClick(params.data);
      }}
      data-testid="index-button-material-action-v2"
    >
      <LinkOutlined />
    </button>
  );

  const requestBtnDisabledHint = () => {
    const strCannotRequestDurations = "Cannot request Lead Time:";

    if (!isAssigneePresent)
      return (
        <div className="w-[200px] text-sm">
          <div>{strCannotRequestDurations}</div>
          <br />
          <div>No subcontractor assigned.</div>
        </div>
      );
    if (isAssigneeUnregistered)
      return (
        <div className="w-[200px] text-sm">
          <div>{strCannotRequestDurations}</div>
          <br />
          <div>Unregistered subcontractor assigned.</div>
        </div>
      );

    if (assigneeIsInactive)
      return (
        <div className="w-[200px] text-sm">
          <div>{strCannotRequestDurations}</div>
          <br />
          <div>Assigned subcontractor is inactive.</div>
        </div>
      );

    if (requestAlreadySent) {
      return (
        <div className="w-[200px] text-sm">
          <div>Request Lead Time:</div>
          <div className="italic">Request sent.</div>
          <br />
          <div className="italic">Click to resend.</div>
        </div>
      );
    }
    return <div className="w-[200px] text-sm">Request Lead Time</div>;
  };

  const requestDurationButton = () => {
    if (!isRequestLeadTimeFeatureEnabled) return null;
    if (!canRequestLeadTime) return null;

    if (
      isAssigneePresent &&
      !assigneeIsInactive &&
      !isAssigneeUnregistered &&
      requestAlreadySent
    ) {
      return (
        <Popover
          placement="left"
          content={<div>{requestBtnDisabledHint()}</div>}
        >
          <div>
            <button
              type="button"
              className="ciq-icon-button !p-1.5 text-ciq-neutral-600 hover:text-ciq-primary-100"
              onClick={() => {
                onRequestDurationClick(params.data);
              }}
              data-testid="index-button-person-check-request-duration"
            >
              <PersonCheckIcon />
            </button>
          </div>
        </Popover>
      );
    }

    return (
      <Popover placement="left" content={<div>{requestBtnDisabledHint()}</div>}>
        <div>
          <button
            type="button"
            className={`ciq-icon-button !p-1.5 ${
              requestBtnDisabled
                ? "text-ciq-gray-400-alt hover:text-ciq-gray-400-alt"
                : "text-ciq-primary-800 hover:text-ciq-primary-800"
            }`}
            onClick={() => {
              onRequestDurationClick(params.data);
            }}
            disabled={requestBtnDisabled}
            data-testid="index-button-person-edit-request-duration"
          >
            <PersonEditIcon />
          </button>
        </div>
      </Popover>
    );
  };

  const showExtractedSource = (
    <Popover
      content={
        data.extraction_datum?.id ? (
          <Space
            className="cursor-pointer"
            onClick={() => {
              history.replace(
                `/project/${projectDetails.id}/materials/extracted`,
                {
                  name: "Extraction Data",
                  extraction: data.extraction_datum
                }
              );
            }}
          >
            Show Source
          </Space>
        ) : null
      }
      placement="left"
    >
      <MoreOutlined />
    </Popover>
  );

  const commentsButton = (
    <button
      type="button"
      className="ciq-icon-button !p-1.5"
      onClick={() => {
        onCommentClick({
          sourceRowIndex: params?.node?.sourceRowIndex
        });
      }}
      data-testid="action-button-material-comments"
    >
      <CommentIcon />
    </button>
  );

  return (
    <div className="flex items-center justify-end">
      {canInlineEditMaterialGeneralInfoAndParticipants ? linkButton : null}
      {requestDurationButton()}
      {isMaterialExtractionEnabled && data.extraction_datum?.id
        ? showExtractedSource
        : null}
      {commentsButton}
    </div>
  );
};

export function ActionsCellRenderer(params: any) {
  const { data, context } = params;
  const { projectId, toolTipMsg, history, isViewLinkingPage } = context;

  return (
    <div>
      {isViewLinkingPage ? (
        <Button
          className="icon-link-arrow"
          icon={<LinkIconLogPage />}
          type="link"
          onClick={() => {
            history.push(
              `/project/${projectId}/schedule/submittal-linking?&SubmittalId=${data.id}`
            );
          }}
          data-testid="submittal-link-button"
        />
      ) : null}
      {data?.redirect_url && (
        <Tooltip placement="right" title={toolTipMsg}>
          <Button
            className="icon-link-arrow button-spacing"
            icon={<ArrowUpBox />}
            color="default"
            type="link"
            onClick={() => {
              window.open(data?.redirect_url, "_blank");
            }}
            data-testid="submittal-redirect-button"
          />
        </Tooltip>
      )}
    </div>
  );
}

export const logPageIDLinkCellRenderer = (id: string, url: string) => {
  return (
    <Link className="gridIdLink" to={url} data-testid="index-link-log-page-id">
      <TruncatedTextWithTooltip text={id} />
    </Link>
  );
};

export const criticalActivityIndicatorCellRenderer = (params: any) => {
  const { data } = params;
  if (data?.critical && data?.type !== GanttActivityTypes.Wps) {
    return (
      <div className="flex w-full h-full items-center justify-start ml-1.5">
        <Tooltip title="Critical Activity">
          <CriticalActivityIndicator />
        </Tooltip>
      </div>
    );
  }
  return "";
};
export const dummyColumnDef = {
  colId: "ag-Grid-SelectionColumn",
  headerName: "",
  width: 20,
  maxWidth: 20,
  minWidth: 20,
  resizable: false,
  filter: false,
  sortable: false,
  suppressHeaderMenuButton: true,
  suppressColumnsToolPanel: true
} as ColDef;

export const dummyColumnDef2 = {
  colId: "ag-Grid-SelectionColumn",
  headerName: "",
  width: 40,
  maxWidth: 40,
  minWidth: 40,
  resizable: false,
  filter: false,
  sortable: false,
  suppressHeaderMenuButton: true,
  suppressColumnsToolPanel: true
} as ColDef;

export function LBSCellRenderer(params: any) {
  const { value, context, node, column, data } = params;

  const selectedLocations: { id: string; path: string }[] =
    value && value.length > 0
      ? [...value].sort((a, b) => a.path.localeCompare(b.path))
      : [];

  const toShow = selectedLocations.slice(0, 2);
  const remaining = selectedLocations.slice(2, selectedLocations.length);

  const locationTag = (location: any) => {
    return (
      <Tag color="default" key={location.id} title={location.path}>
        {location.path}
      </Tag>
    );
  };

  const popoverContent = (locations: any[]) => {
    return (
      <div className="max-w-[300px] max-h-[300px] overflow-y-auto flex flex-wrap items-center gap-y-1 gap-x-0 leading-none py-2">
        {locations.map((location: any) => locationTag(location))}
      </div>
    );
  };

  return (
    <CellChilderenRendererWithEditButton
      onEditCell={() => {
        context.onEditCell?.({
          rowIndex: node.rowIndex!,
          colKey: column!.getId(),
          data
        });
      }}
    >
      <div className="h-full max-w-full max-h-full min-w-[250px] overflow-hidden py-1 flex items-end">
        <div className="flex flex-wrap gap-y-1 gap-x-0 leading-none items-center overflow-hidden">
          {toShow.map((location: any, index: number) => {
            if (index !== toShow.length - 1) {
              return locationTag(location);
            }
            return (
              <div className="flex" key={location.id}>
                {locationTag(location)}
                {remaining.length > 0 && (
                  <Popover content={popoverContent(remaining)}>
                    <div className="py-1 text-left">+{remaining.length}</div>
                  </Popover>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </CellChilderenRendererWithEditButton>
  );
}
